import { gsap } from "gsap";
import { IDestroyOptions, Sprite, Text, TextStyle, Texture, utils } from "pixi.js";
import { ICanvasScaler } from "../../../../../canvas/ICanvasScaler";
import { PromiseCompletionSource } from "../../../../../shared/async/PromiseCompletionSource";
import { BaseScalableView } from "../../../common/BaseScalableView";
import { GameFont } from "../../../common/GameFont";
import { ButtonWithScaleAnimation } from "../../../common/components/ButtonWithScaleAnimation";
import { CommonResourcesPackage } from "../../../common/resources/CommonResourcesPackage";
import { FadeScreen } from "../../../game/components/FadeScreen";
import { IFocusable } from "../../../layers/IFocusable";
import { textInputFactory } from "../../../common/components/input/TextInputFactory";
import { ITextInput } from "../../../common/components/input/ITextInput";
import { EditPriceResourcePackage } from "../resources/EditPriceResourcePackage";
import { ILogger } from "../../../../logger/ILogger";
import { AsyncOperationNotification } from "../../../common/components/AsyncOperationNotification";
import { IEditPriceController } from "../controllers/IEditPriceController";
import { CancellationToken } from "async/CancellationToken";
import { TextWithUnderline } from "../../../game/components/TextWithUnderline";
import { GitBookPage, Redirector } from "../../../../../tools/Redirector";
import { StringFormatExtention } from "../../../../../extensions/StringFormatExtention";
import { ShowEditPriceSceneParams } from "../scene/ShowEditPriceSceneParams";
import { EditPriceContainer } from "../components/EditPriceContainer";
import { FullscreenInputBlocker } from "../../../game/components/FullscreenInputBlocker";

export class EditPricePopupView extends BaseScalableView<boolean> implements IFocusable {
    private static readonly MAX_PRICE = 1300.0;
    private static readonly GREAT_BUTTON_TEXT = "UPDATE PRICE";
    private static readonly LIST_IN_TEXT = "LIST IN";
    private static readonly PURCHASE_PRICE = "PURCHASE PRICE: ";
    private readonly regex: RegExp = /^([0-9]{0,4})(\.([0-9]{0,2}))?/;

    public readonly focusable: boolean = true;

    private readonly cancellationToken: CancellationToken;
    private readonly commonResources: CommonResourcesPackage;
    private readonly editPriceResources: EditPriceResourcePackage;
    private readonly editPriceController: IEditPriceController;
    private readonly showParams: ShowEditPriceSceneParams;
    private readonly logger: ILogger;
    private readonly operationNotification: AsyncOperationNotification;
    private fadeScreen: FadeScreen;
    private frameContainer: EditPriceContainer;
    private initialCostText: Text;
    private light: Sprite;
    private saveButton: ButtonWithScaleAnimation;
    private saveButtonText: Text;
    private listInButton: ButtonWithScaleAnimation;
    private listButtonText: Text;
    private listInButtonIcon: Sprite;
    private closeButton: ButtonWithScaleAnimation;
    private inputBg: Sprite;
    private input: ITextInput;
    private previousInput: string;
    private usdText: Text;
    private additionalInfoText: Text;
    private inputInitialPosX: number;
    private currentPrice: number;

    private showViewAnimationTimeLine: gsap.core.Timeline;

    public constructor(
        operationControls: PromiseCompletionSource<boolean>,
        commonResources: CommonResourcesPackage,
        editPriceResources: EditPriceResourcePackage,
        canvasScaler: ICanvasScaler,
        editPriceController: IEditPriceController,
        showParams: ShowEditPriceSceneParams,
        logger: ILogger,
        operationNotification: AsyncOperationNotification
    ) {
        super(operationControls, canvasScaler);
        this.cancellationToken = showParams.cancellationToken;
        this.commonResources = commonResources;
        this.editPriceResources = editPriceResources;
        this.editPriceController = editPriceController;
        this.showParams = showParams;
        this.logger = logger;
        this.operationNotification = operationNotification;
        this.showViewAnimationTimeLine = new gsap.core.Timeline({ paused: true });
        this.showViewAnimationTimeLine.fromTo(this, { alpha: 0 }, { alpha: 1, duration: 0.2, ease: "power1.in" });
        this.currentPrice = 0;
        this.previousInput = "";
        this.build();
        this.layout(this.canvasScaler.referenceWidth, this.canvasScaler.referenceHeight);
        this.cancellationToken.onCanceled.add(this.onCancellationRequested, this);
        this.additionalInfoText.on("pointerup", this.onAdditionalInfoClick, this);
    }

    public start(): void {
        this.showAnimation();
        const disabled = this.input.value.length === 0 || Number(this.input.value) === 0;
        this.saveButton.visible = !this.showParams.isAddToMarketplace;
        this.listInButton.visible = this.showParams.isAddToMarketplace;
        this.saveButton.eventMode = disabled ? "none" : "static";
        this.saveButton.defaultView.tint = disabled ? "#4E544E" : "#ffffff";
        this.saveButtonText.tint = this.saveButton.defaultView.tint;
        this.listInButton.eventMode = disabled ? "none" : "static";
        this.listInButton.defaultView.tint = disabled ? "#4E544E" : "#ffffff";
        this.listButtonText.tint = this.listInButton.defaultView.tint;
        this.listInButtonIcon.tint = this.listInButton.defaultView.tint;
    }

    private build(): void {
        this.fadeScreen = new FadeScreen(0.1, 0, 0.8, this.canvasScaler.referenceWidth, this.canvasScaler.referenceHeight);
        this.fadeScreen.eventMode = "static";
        this.addChild(this.fadeScreen);

        this.frameContainer = new EditPriceContainer(this.editPriceResources);
        this.addChild(this.frameContainer);

        this.light = new Sprite(this.editPriceResources.assets_views_edit_price_images_light_png);
        this.addChild(this.light);

        this.saveButton = new ButtonWithScaleAnimation(this.commonResources.assets_common_ui_button_green_png);
        this.saveButton.scale.set(0.8);
        this.saveButtonText = new Text(EditPricePopupView.GREAT_BUTTON_TEXT, new ButtonTextStyle());
        this.saveButtonText.position.set(this.saveButton.width * 0.5 - this.saveButtonText.width * 0.5 + 30, 40);
        this.saveButton.defaultView.addChild(this.saveButtonText);
        this.saveButton.onUp.add(this.saveButtonClickHandle, this);
        this.addChild(this.saveButton);

        this.listInButton = new ButtonWithScaleAnimation(this.commonResources.assets_common_ui_button_green_png);
        this.listInButton.scale.set(0.8);
        this.listButtonText = new Text(EditPricePopupView.LIST_IN_TEXT, new ListInButtonTextStyle());
        this.listButtonText.position.set(75, 50);
        this.listInButton.defaultView.addChild(this.listButtonText);
        this.listInButtonIcon = new Sprite(this.commonResources.assets_common_ui_vector_png);
        this.listInButtonIcon.width = 66;
        this.listInButtonIcon.height = 60;
        this.listInButtonIcon.position.set(229, 50);
        this.listInButton.defaultView.addChild(this.listInButtonIcon);
        this.listInButton.onUp.add(this.saveButtonClickHandle, this);
        this.addChild(this.listInButton);

        this.closeButton = new ButtonWithScaleAnimation(this.commonResources.assets_common_x_png);
        this.closeButton.onUp.once(() => this.closeButtonClickHandle(false));
        this.addChild(this.closeButton);

        this.initialCostText = new Text(EditPricePopupView.PURCHASE_PRICE, new InitialCostTextStyle());
        this.updateInitialCost();
        this.addChild(this.initialCostText);

        this.inputBg = new Sprite(this.editPriceResources.assets_views_edit_price_images_price_bg_png);
        this.addChild(this.inputBg);
        const textInputBackground = new Sprite(Texture.EMPTY);
        textInputBackground.height = this.editPriceResources.assets_views_edit_price_images_price_bg_png.height;
        this.input = textInputFactory({
            bg: textInputBackground,
            textStyle: new InputTextStyle(),
            placeholder: "ENTER YOUR PRICE",
            align: "left",
            maxLength: 7,
            padding: {
                left: 3,
                top: 2
            },
            value: ""
        }, utils.isMobile.android.device ? "numeric" : "text");
        this.input.onChange.connect(this.inputChangeHandle.bind(this));
        this.input.onStartEditing.add(this.onStartEditing, this);
        this.input.onEnter.connect(this.blurHandle.bind(this));
        this.addChild(this.input);

        this.usdText = new Text(this.editPriceResources.assets_views_edit_price_text_note_text_txt.toUpperCase(), new UsdTextStyle());
        this.addChild(this.usdText);

        this.additionalInfoText = new TextWithUnderline(`PLEASE NOTE`, this.usdText.style, 0, -3);
        this.additionalInfoText.style.fontSize = 34;
        this.additionalInfoText.eventMode = "static";
        this.addChild(this.additionalInfoText);
    }

    private updateInitialCost(): void {
        this.initialCostText.visible = this.showParams.purchaseHistory.length > 0;
        if (this.showParams.purchaseHistory?.length > 0) {
            const firstHistory = this.showParams.purchaseHistory[0];
            const price = firstHistory.price.usd != null ? firstHistory.price.usd.toFixed(2) : firstHistory.price.mon.toFixed(2);
            const currency = firstHistory.price.usd != null ? "$" : "MON";
            this.initialCostText.text = `${EditPricePopupView.PURCHASE_PRICE}${currency}${StringFormatExtention.formatWithComma(price)}`;
        }
    }

    private async showAnimation(): Promise<void> {
        this.fadeScreen.fadeIn();
        await this.showViewAnimationTimeLine.play();
    }

    private async hideViewAnimation(): Promise<void> {
        await this.showViewAnimationTimeLine.reverse();
    }

    private inputChangeHandle(): void {
        if (this.input.value.length > 0 && this.input.value[0] === ".") {
            this.input.value = "0.";
        }
        if (this.input.value.length > 1 && this.input.value[0] === "0" && !Number.isNaN(parseInt(this.input.value[1]))) {
            let symbolsToCut = 1;
            for (let i = 1; i < this.input.value.length; i++) {
                if (this.input.value[i] !== "0") {
                    break;
                }
                symbolsToCut++;
            }
            this.input.value = this.input.value.slice(symbolsToCut, this.input.value.length);
        }
        const dotIndex = this.input.value.indexOf(".");
        const lastIndex = this.input.value.length - 1;
        if (dotIndex !== -1 && lastIndex - dotIndex > 1 && this.input.value[lastIndex] === "0") {
            this.input.value = this.input.value.slice(0, lastIndex);
        }
        this.input.value = this.regex.test(this.input.value) ? this.input.value.match(this.regex)[0] : this.previousInput;
        if (Number(this.input.value) > EditPricePopupView.MAX_PRICE) {
            this.input.value = `${EditPricePopupView.MAX_PRICE.toFixed(2)}`;
        }
        this.previousInput = this.input.value;

        this.currentPrice = Number(this.input.value);
        const disabled = this.input.value.length === 0 || Number(this.input.value) === 0 || (this.input.value.length > 0 && this.input.value[this.input.value.length - 1] === ".");
        this.saveButton.eventMode = disabled ? "none" : "static";
        this.listInButton.eventMode = disabled ? "none" : "static";
        this.saveButton.defaultView.tint = disabled ? "#4E544E" : "#ffffff";
        this.listInButton.defaultView.tint = disabled ? "#4E544E" : "#ffffff";
        this.saveButtonText.tint = this.saveButton.defaultView.tint;
        this.listButtonText.tint = this.listInButton.defaultView.tint;
        this.listInButtonIcon.tint = this.listInButton.defaultView.tint;
        this.input.position.x = this.frameContainer.x + this.frameContainer.width * 0.5 - this.input.width * 0.5;
    }

    private onStartEditing(): void {
        if (this.input.value.length === 0) {
            this.input.position.x = this.frameContainer.x + this.frameContainer.width * 0.5 - 20;
        }
    }

    private blurHandle(): void {
        if (this.input.value.length === 0) {
            this.input.position.x = this.inputInitialPosX;
        }
    }

    private async saveButtonClickHandle(): Promise<void> {
        const fullScreenInputBlocker = new FullscreenInputBlocker(this.canvasScaler);
        this.addChild(fullScreenInputBlocker);
        if (this.showParams.currentPrice === parseFloat(this.input.value) && !this.showParams.isAddToMarketplace) {
            this.closeButtonClickHandle(false);
            return;
        }

        if (this.showParams.purchaseHistory?.length > 0) {
            const firstHistory = this.showParams.purchaseHistory[0];
            const price = firstHistory.price.usd;
            if (price != null && this.currentPrice <= price) {
                const result = await this.editPriceController.showWarningMessagePopup();
                if (!result) {
                    fullScreenInputBlocker.destroy();
                    return;
                }
            }
        }

        if (this.showParams.isAddToMarketplace) {
            try {
                this.operationNotification.show();
                await this.editPriceController.listBuildingOnMarketplace(this.showParams.buildingId, this.showParams.tokenId, this.currentPrice);
                this.visible = false;
                this.operationNotification.hide();
                await this.editPriceController.showCongratulationPopup(this.showParams.isAddToMarketplace, this.cancellationToken);
                this.closeButtonClickHandle(true);
            } catch (error) {
                this.logger.error(error);
            } finally {
                fullScreenInputBlocker.blocked = false;
                this.operationNotification.hide();
            }
        } else {
            const result = await this.editPriceController.editBuildingPriceOnMarketplace(this.showParams.buildingId, this.showParams.orderId, this.showParams.tokenId, this.currentPrice);
            if (result) {
                this.visible = false;
                await this.editPriceController.showCongratulationPopup(false, this.cancellationToken);
                this.closeButtonClickHandle(result);
            }
        }
        fullScreenInputBlocker.blocked = false;
    }

    private layout(_width: number, _height: number): void {
        this.frameContainer.position.set(this.width * 0.5 - this.frameContainer.width * 0.5, 128);
        this.saveButton.position.set(265, 715);
        this.listInButton.position.set(265, 715);
        this.closeButton.position.set(this.frameContainer.x + this.frameContainer.width * 0.5 - this.closeButton.width * 0.5, 905);
        this.initialCostText.position.set(this.frameContainer.x + this.frameContainer.width * 0.5 - this.initialCostText.width * 0.5, 321);
        this.inputBg.position.set(this.frameContainer.x + this.frameContainer.width * 0.5 - this.inputBg.width * 0.5, 362);
        this.input.position.set(this.frameContainer.x + this.frameContainer.width * 0.5 - this.input.width * 0.5, 363);
        this.inputInitialPosX = this.input.position.x;
        this.usdText.position.set(this.frameContainer.x + this.frameContainer.width * 0.5 - this.usdText.width * 0.5, 477);
        this.additionalInfoText.position.set(this.frameContainer.x + (this.frameContainer.width - this.additionalInfoText.width) * 0.5, 434);
    }

    private onCancellationRequested(): void {
        this.operationControls.complete(false);
        this.destroy();
    }

    private onAdditionalInfoClick(): void {
        Redirector.openGitBook(GitBookPage.BUYING_AND_SELLING);
    }

    private async closeButtonClickHandle(result: boolean): Promise<void> {
        this.closeButton.eventMode = "none";
        await this.hideViewAnimation();
        this.operationControls.complete(result);
    }

    public override destroy(options?: boolean | IDestroyOptions): void {
        this.additionalInfoText.off("pointerup", this.onAdditionalInfoClick, this);
        this.cancellationToken.onCanceled.remove(this.onCancellationRequested, this);
        this.saveButton.onUp.remove(this.saveButtonClickHandle, this);
        this.listInButton.onUp.remove(this.saveButtonClickHandle, this);
        this.showViewAnimationTimeLine?.kill();
        this.input.onChange.disconnectAll();
        this.input.onEnter.disconnectAll();
        this.input.onStartEditing.remove(this.onStartEditing, this);
        this.input.off("touchstart");
        super.destroy(options);
    }
}

class ButtonTextStyle extends TextStyle {
    public constructor() {
        super(new TextStyle({
            fill: "#ffffff",
            fontFamily: GameFont.DEFAULT_FONT,
            dropShadow: true,
            dropShadowAlpha: 0.5,
            dropShadowAngle: 0.5,
            dropShadowBlur: 5,
            dropShadowDistance: 4,
            fontSize: 66,
            leading: 15
        }));
    }
}

class InputTextStyle extends TextStyle {
    public constructor() {
        super(new TextStyle({
            fill: "#ffffff",
            fontFamily: GameFont.BEBAS_NEUE_PRO_SMERG_BOLD,
            fontSize: 48
        }));
    }
}

class UsdTextStyle extends TextStyle {
    public constructor() {
        super(new TextStyle({
            fill: "#ffffff",
            fontFamily: GameFont.DEFAULT_FONT,
            fontSize: 34,
            lineHeight: 39,
            letterSpacing: 1,
            strokeThickness: 2
        }));
    }
}

class ListInButtonTextStyle extends TextStyle {
    public constructor() {
        super(new TextStyle({
            align: "center",
            fill: "#ffffff",
            fontFamily: GameFont.DEFAULT_FONT,
            fontSize: 66,
            dropShadow: true,
            dropShadowColor: "#0d0606",
            dropShadowAlpha: 0.3,
            dropShadowAngle: 0.82859,
            dropShadowBlur: 5
        }));
    }
}

class InitialCostTextStyle extends TextStyle {
    public constructor() {
        super({
            fontFamily: GameFont.DEFAULT_FONT,
            fill: [
                "#a8fd09",
                "#15ce14"
            ],
            fontSize: 34,
            align: "center",
        });
    }
}
